import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../store';
import { setComments, addComment, updateComment, removeComment, setLoading, setError } from '../store/slices/commentSlice';
import { commentsApi } from '../api/comments';
import type { CreateCommentRequest, UpdateCommentRequest, CommentPaginatedResponse } from '../types/comment';

export const useComments = (postId?: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const { items, currentPostComments, loading, error } = useSelector(
    (state: RootState) => state.comments
  );

  const comments = postId ? (currentPostComments[postId] || []) : items;

  const fetchComments = useCallback(async (targetPostId: string, page: number = 0, size: number = 20, includeDeleted: boolean = false) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      const response: CommentPaginatedResponse = await commentsApi.getCommentsByPostId(targetPostId, page, size, includeDeleted);
      dispatch(setComments({ postId: targetPostId, comments: response.content }));
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch comments';
      dispatch(setError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const createComment = useCallback(async (targetPostId: string, data: CreateCommentRequest) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      const newComment = await commentsApi.createComment(targetPostId, data);
      dispatch(addComment(newComment));
      return newComment;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create comment';
      dispatch(setError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const updateCommentById = useCallback(async (commentId: string, data: UpdateCommentRequest) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      const updatedComment = await commentsApi.updateComment(commentId, data);
      dispatch(updateComment(updatedComment));
      return updatedComment;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update comment';
      dispatch(setError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const deleteCommentById = useCallback(async (commentId: string, targetPostId: string) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      await commentsApi.deleteComment(commentId);
      dispatch(removeComment({ commentId, postId: targetPostId }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete comment';
      dispatch(setError(errorMessage));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  return {
    comments,
    loading,
    error,
    fetchComments,
    createComment,
    updateComment: updateCommentById,
    deleteComment: deleteCommentById,
  };
};

